import Dropdown from "common/Dropdown/Dropdown.tsx";
import React, { useState } from "react";
import { ActionsDropdownIcon } from "common/styles.ts";
import { sharedLib } from "@shared/services/shared.service.ts";
import { displayResultMessage } from "utils/misc.tsx";
import {
  ExperienceConnectionStatus,
  GroupData,
  LanguageNameSpaces,
  PlatformType,
} from "@shared/types";
import CreatePersonalGroupModal from "features/bots/Groups/CreatePersonalGroupModal.tsx";
import { ItemType } from "antd/es/menu/interface";
import useConfirmModal from "common/Modal/useConfirmModal.tsx";
import EditOutlined from "@ant-design/icons/EditOutlined";
import DeleteOutlined from "@ant-design/icons/DeleteOutlined";
import styled from "styled-components";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { IS_WHOP } from "common/constants.ts";
import DisconnectOutlined from "@ant-design/icons/DisconnectOutlined";
import { ApiOutlined } from "@ant-design/icons";

interface MyGroupActionsProps {
  group: GroupData;
}

const MyGroupActions = ({ group }: MyGroupActionsProps) => {
  const { confirmModalContextHolder, openConfirmModal } = useConfirmModal({});
  const { t } = useTypedTranslation(LanguageNameSpaces.GROUPS);

  const [deleteSubGroup] =
    sharedLib.sharedApi.settings.useDeleteSubGroupMutation();
  const [changeExperienceConnectionStatus] =
    sharedLib.sharedApi.settings.useChangeExperienceConnectionStatusMutation();

  const [openEditModal, setOpenEditModal] = useState(false);
  const isEditableGroup =
    group.is_personal || group.platform_type === PlatformType.CORNIX;

  const handleChangeStatus = () => {
    if (isEditableGroup) {
      displayResultMessage(
        deleteSubGroup({ sub_group_id: group.id }).unwrap(),
        t("actions.personal_group_deleted")
      ).catch(() => {});
    } else {
      displayResultMessage(
        changeExperienceConnectionStatus({
          status: group.is_active
            ? ExperienceConnectionStatus.DISCONNECTED
            : ExperienceConnectionStatus.CONNECTED,
          sub_group_id: group.id,
        }).unwrap(),
        group.is_active
          ? t("actions.disconnected_from_whop")
          : t("actions.reconnected_to_whop")
      ).catch(() => {});
    }
  };

  const getMenuItems = () => {
    const menuItems: ItemType[] = [];
    if (isEditableGroup) {
      menuItems.push({
        key: "edit",
        label: t("actions.edit_name"),
        icon: <EditOutlined />,
        onClick: () => setOpenEditModal(true),
      });
      menuItems.push({
        key: "delete",
        label: t("actions.delete"),
        icon: <DeleteOutlined />,
        onClick: () => {
          openConfirmModal(t("actions.delete_confirm"), handleChangeStatus);
        },
      });
    } else if (IS_WHOP) {
      if (group.is_active) {
        menuItems.push({
          key: "disconnect",
          label: t("actions.disconnect_from_whop"),
          icon: <DisconnectOutlined />,
          onClick: () => {
            openConfirmModal(
              t("actions.disconnect_from_whop_confirm"),
              handleChangeStatus
            );
          },
        });
      } else {
        menuItems.push({
          key: "reconnect",
          label: t("actions.reconnect_group"),
          icon: <ApiOutlined />,
          onClick: () => {
            openConfirmModal(
              t("actions.reconnect_group_confirm"),
              handleChangeStatus
            );
          },
        });
      }
    }
    return menuItems;
  };

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Dropdown
        menu={{ items: getMenuItems() }}
        trigger={["click"]}
        title={t("actions.title")}
      >
        <StyledActionsDropdownIcon $horizontal={true} />
      </Dropdown>
      {isEditableGroup && (
        <CreatePersonalGroupModal
          open={openEditModal}
          closeModal={() => setOpenEditModal(false)}
          group={group}
        />
      )}
      {confirmModalContextHolder}
    </div>
  );
};

export default MyGroupActions;

const StyledActionsDropdownIcon = styled(ActionsDropdownIcon)`
  top: -4px;
  position: relative;
`;
